import {
  SEVERITY_TABLE_COLUMN,
  TABLE_COLUMNS,
  TRIVY_LABELS,
  TRIVY_SEVERITIES,
  TableRow,
  TrivySeverity,
  VulnerabilityReportTable,
  severityRank,
} from '../types';

import { VulnerabilityRow } from './types';

// Cells are positional, so lookups go through columnDefinitions by name -
// hardcoded indices would break silently if the CRD reorders its columns.
export function fromTable(
  table?: VulnerabilityReportTable
): Array<VulnerabilityRow> {
  if (!table?.rows?.length) {
    return [];
  }

  const columnIndex = indexColumns(table.columnDefinitions);

  return table.rows
    .map((row) => toRow(row, columnIndex))
    .filter((row): row is VulnerabilityRow => row !== undefined)
    .sort(bySeverityThenName);
}

function indexColumns(
  columns: Array<{ name: string }> = []
): Map<string, number> {
  return new Map(columns.map((column, index) => [column.name, index]));
}

function toRow(
  row: TableRow,
  columnIndex: Map<string, number>
): VulnerabilityRow | undefined {
  const metadata = row.object?.metadata;
  // No object metadata means the row cannot be attributed to a workload.
  if (!metadata) {
    return undefined;
  }

  const labels = metadata.labels ?? {};
  const counts = toCounts(row, columnIndex);
  const namespace =
    labels[TRIVY_LABELS.resourceNamespace] ?? metadata.namespace;
  const workload = labels[TRIVY_LABELS.resourceName] ?? metadata.name;

  return {
    id: `${metadata.namespace}/${metadata.name}`,
    name: `${namespace}/${workload}`,
    namespace,
    workload,
    resourceKind: labels[TRIVY_LABELS.resourceKind] ?? '',
    container: labels[TRIVY_LABELS.containerName] ?? '',
    image: toImage(row, columnIndex),
    counts,
    total: TRIVY_SEVERITIES.reduce((sum, s) => sum + counts[s], 0),
    highestSeverity: TRIVY_SEVERITIES.find((s) => counts[s] > 0),
    age: cellText(row, columnIndex, TABLE_COLUMNS.age),
  };
}

function toCounts(
  row: TableRow,
  columnIndex: Map<string, number>
): Record<TrivySeverity, number> {
  return {
    CRITICAL: cellCount(row, columnIndex, SEVERITY_TABLE_COLUMN.CRITICAL),
    HIGH: cellCount(row, columnIndex, SEVERITY_TABLE_COLUMN.HIGH),
    MEDIUM: cellCount(row, columnIndex, SEVERITY_TABLE_COLUMN.MEDIUM),
    LOW: cellCount(row, columnIndex, SEVERITY_TABLE_COLUMN.LOW),
    UNKNOWN: cellCount(row, columnIndex, SEVERITY_TABLE_COLUMN.UNKNOWN),
    NONE: cellCount(row, columnIndex, SEVERITY_TABLE_COLUMN.NONE),
  };
}

function toImage(row: TableRow, columnIndex: Map<string, number>): string {
  const repository = cellText(row, columnIndex, TABLE_COLUMNS.repository) ?? '';
  const tag = cellText(row, columnIndex, TABLE_COLUMNS.tag);

  return tag ? `${repository}:${tag}` : repository;
}

function cell(
  row: TableRow,
  columnIndex: Map<string, number>,
  column: string
): unknown {
  const index = columnIndex.get(column);
  return index === undefined ? undefined : row.cells?.[index];
}

function cellCount(
  row: TableRow,
  columnIndex: Map<string, number>,
  column: string
): number {
  const value = Number(cell(row, columnIndex, column));
  return Number.isFinite(value) ? value : 0;
}

function cellText(
  row: TableRow,
  columnIndex: Map<string, number>,
  column: string
): string | undefined {
  const value = cell(row, columnIndex, column);
  return typeof value === 'string' && value !== '' ? value : undefined;
}

function bySeverityThenName(a: VulnerabilityRow, b: VulnerabilityRow): number {
  const bySeverity = highestRank(a) - highestRank(b);
  if (bySeverity !== 0) {
    return bySeverity;
  }
  return a.name.localeCompare(b.name);
}

function highestRank(row: VulnerabilityRow): number {
  return row.highestSeverity
    ? severityRank(row.highestSeverity)
    : TRIVY_SEVERITIES.length;
}
